import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  SlideOutPanelShell,
  slideOutPanelShellStyles as styles,
  useExitRetained,
  useSlideOutPanelChrome,
} from '@cowbell/ui';
import type { Source, CveMetadata } from '../../types';
import { useCveProfile } from '../../hooks/useCveProfile';
import { renderPanelBody } from './panel-body-states';
import CveDetailBody from './CveDetailBody';

interface CvePanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly source: Source | null;
  /** Width of the Ask Moo chat container (px) */
  readonly chatWidth: number;
  /** Right offset of the chat container (px) — needed for floating mode */
  readonly chatRight?: number;
}

/**
 * Fixed-position CVE intelligence panel docked to the LEFT of the Ask Moo
 * chat sidebar. Shares its chrome with ThreatActorPanel via
 * useSlideOutPanelChrome + SlideOutPanelShell (same CSS module too) and its
 * body state-gating via renderPanelBody — only the header content and
 * detail component differ. Seeds its header instantly from the citation
 * metadata while the rich profile lazy-loads by cveId.
 */
export default function CvePanel({
  isOpen,
  onClose,
  source,
  chatWidth,
  chatRight = 0,
}: Readonly<CvePanelProps>) {
  // On close the caller clears `source` in the same turn; the panel keeps what it showed while it slides away.
  const { value: shown, exiting } = useExitRetained(source, isOpen && !!source);
  const meta = shown?.metadata as CveMetadata | undefined;

  const chrome = useSlideOutPanelChrome({
    isOpen,
    hasSource: !!source,
    onClose,
    chatWidth,
    chatRight,
  });

  const live = chrome.visible || exiting;
  const cveId = live ? meta?.cveId ?? null : null;

  const { profile, isLoading, isError, isNotFound, refetch } = useCveProfile(
    cveId,
    live ? meta : undefined
  );

  return (
    <SlideOutPanelShell
      chrome={chrome}
      onClose={onClose}
      styles={styles}
      headerTitle={meta?.cveId ?? 'CVE Intelligence'}
      headerMeta={profile?.severity}
      headerLink={
        shown?.url && (
          <a
            className={styles.headerBtn}
            href={shown.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open NVD page"
            aria-label="Open NVD page"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        )
      }
    >
      {renderPanelBody({
        isNotFound,
        isError,
        profile,
        isLoading,
        notFoundMessage: `CVE ${meta?.cveId} not found in the knowledge base.`,
        errorMessage: 'Failed to load CVE profile.',
        loadingMessage: 'Loading CVE intelligence…',
        refetch,
        styles,
        renderDetail: (p) => <CveDetailBody profile={p} />,
      })}
    </SlideOutPanelShell>
  );
}
